import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReportes } from '../hooks/useReportes';
import { Card, Button } from '../components/ui';
import type { ReporteFiltros } from '../types';

export const ReportesListMovilView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState<ReporteFiltros>({
    page: 1,
    per_page: 20,
  });

  const {
    data: reportesData,
    isLoading,
    error,
    refetch
  } = useReportes(filtros);

  const reportes = reportesData?.items || [];
  const total = reportesData?.total || 0;

  // Estados para colores
  const getEstadoColor = (estado: string) => {
    switch (estado?.toLowerCase()) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'aprobado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rechazado':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-4">{t('errors.load_error')}</p>
          <Button onClick={() => refetch()}>{t('common.retry', 'Reintentar')}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header Sticky */}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('reports.title')}</h1>
            <p className="text-sm text-gray-600">{total} {t('common.total')}</p>
          </div>
          <Button
            size="sm"
            onClick={() => navigate('/reportes/crear')}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            ➕ Nuevo
          </Button>
        </div>
      </div>

      {/* Lista de Reportes */}
      <div className="p-4 space-y-3">
        {reportes.length === 0 ? (
          <Card className="bg-white">
            <div className="p-8 text-center">
              <svg
                className="w-16 h-16 text-gray-300 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="text-gray-600 mb-4">{t('common.no_data')}</p>
              <Button
                onClick={() => navigate('/reportes/crear')}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {t('reports.new_report')}
              </Button>
            </div>
          </Card>
        ) : (
          reportes.map((reporte) => (
            <Card
              key={reporte.id}
              className="bg-white cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/reportes/${reporte.id}`)}
            >
              <div className="p-4">
                {/* Header del reporte */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-gray-900">
                        #{reporte.numero}
                      </h3>
                      <span
                        className={`text-xs px-2 py-1 rounded-full border font-medium ${getEstadoColor(
                          reporte.estado
                        )}`}
                      >
                        {reporte.estado}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {(() => {
                        const [y, m, d] = reporte.fecha.split('-').map(Number);
                        return new Date(y, m - 1, d).toLocaleDateString('es-CL', {
                          weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                        });
                      })()}
                    </p>
                  </div>
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>

                {/* Información de máquina */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-700">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M3.375 4.5C2.339 4.5 1.5 5.34 1.5 6.375V13.5h12V6.375c0-1.036-.84-1.875-1.875-1.875h-8.25Z" />
                    </svg>
                    <div>
                      <div className="font-medium">{reporte.maquina_codigo}</div>
                      <div className="text-xs text-gray-500">{reporte.maquina_descripcion}</div>
                    </div>
                  </div>

                  {reporte.operador_codigo && (
                    <div className="flex items-center text-gray-700">
                      <svg
                        className="w-4 h-4 mr-2 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      <div>
                        <div className="font-medium">{reporte.operador_codigo}</div>
                        <div className="text-xs text-gray-500">{reporte.operador_descripcion}</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center text-gray-700">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>
                      {reporte.horas_trabajadas.toFixed(1)} {t('reports.worked_hours')}
                    </span>
                  </div>

                  {reporte.total_faenas > 0 && (
                    <div className="flex items-center text-gray-700">
                      <svg
                        className="w-4 h-4 mr-2 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      <span>
                        {reporte.total_faenas} {reporte.total_faenas === 1 ? 'faena' : 'faenas'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Paginación simple */}
      {reportes.length > 0 && (
        <div className="p-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setFiltros((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))
              }
              disabled={filtros.page === 1}
            >
              {t('common.previous')}
            </Button>

            <span className="text-sm text-gray-600">
              {t('common.page')} {filtros.page}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setFiltros((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))
              }
              disabled={reportes.length < (filtros.per_page || 20)}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportesListMovilView;
